/**
 * FullscreenContainer Component
 * Feature: 057-lightbox-fullscreen-oneup
 *
 * Provides browser fullscreen mode using the native Fullscreen API.
 * Hides all browser chrome (tabs, address bar, toolbars) and displays
 * photos on a black background.
 *
 * Usage:
 * ```tsx
 * <FullscreenContainer
 *   isActive={viewMode === 'fullscreen'}
 *   onExit={() => setViewMode('normal')}
 * >
 *   <DetailView2 {...props} />
 * </FullscreenContainer>
 * ```
 */

import React, { useEffect } from 'react';
import { cn } from '@/lib/utils';
import logger from '@/utils/logger';

export interface FullscreenContainerProps {
  /**
   * Whether fullscreen mode is currently active
   */
  isActive: boolean;

  /**
   * Callback when fullscreen mode is exited
   * (via ESC, F11, browser controls, or F key)
   */
  onExit: () => void;

  /**
   * Content to render in fullscreen container (typically DetailView2)
   */
  children: React.ReactNode;

  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * FullscreenContainer manages browser fullscreen state using the native
 * Fullscreen API.
 *
 * Implementation details:
 * - Parent component calls requestFullscreen() synchronously during user gesture
 * - Listens to fullscreenchange event to detect external exits
 * - Syncs React state with browser fullscreen state
 * - Handles exit via document.exitFullscreen()
 * - Cleans up event listeners on unmount
 *
 * Browser Compatibility:
 * - Chrome 15+ (2011)
 * - Firefox 10+ (2012)
 * - Safari 5.1+ (2011)
 * - Edge 12+ (2015)
 * - Mobile Safari: Limited support (out of scope)
 *
 * Performance: Uses native browser API (zero React overhead)
 */
export const FullscreenContainer = React.forwardRef<HTMLDivElement, FullscreenContainerProps>(function FullscreenContainer({
  isActive,
  onExit,
  children,
  className,
}, ref) {

  // Effect 1: Listen to fullscreenchange events to sync state
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;

      // If browser exits fullscreen but React state still thinks we're fullscreen,
      // sync the state by calling onExit
      if (!isCurrentlyFullscreen && isActive) {
        logger.info('Fullscreen', 'Browser exited fullscreen, syncing React state');
        onExit();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isActive, onExit]);

  // Effect 2: Exit fullscreen when isActive changes to false
  // Note: Entry is handled by parent component to ensure synchronous user gesture
  useEffect(() => {
    const exitFullscreen = async () => {
      if (!document.fullscreenElement) {
        // Already exited, nothing to do
        return;
      }

      try {
        await document.exitFullscreen();
        logger.info('Fullscreen', 'Exited fullscreen mode');
      } catch (error) {
        logger.error('Fullscreen', 'Failed to exit fullscreen:', error);
      }
    };

    // Only exit if isActive is false and we're currently in fullscreen
    if (!isActive && document.fullscreenElement) {
      exitFullscreen();
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      className={cn(
        // Full height and width
        'h-full w-full',
        // Black background for fullscreen mode
        'bg-black',
        // Smooth fade transition (200ms per spec)
        'transition-opacity duration-200 ease-in-out',
        // Custom classes
        className
      )}
      data-testid="fullscreen-container"
    >
      {children}
    </div>
  );
});

export default FullscreenContainer;
